import type { LooperProject, Cell } from "./types";
import type { Patch } from "../engine/types";
import { Voice } from "../engine/Voice";
import { midiToFreq } from "../engine/Synth";
import { spawnNoteVoice } from "../engine/spawnVoice";

const LOOKAHEAD = 0.12; // seconds scheduled ahead of the clock
const TICK_MS = 25;

interface LiveNote {
  voice: Voice;
  start: number;
  patchId: string;
}

// Plays a LooperProject: each box (Cell) loops independently on the shared Web
// Audio clock, boxes stack, and live input can be recorded into a focused box.
// Reuses the engine's Voice so looper sounds match the playground exactly.
export class LooperEngine {
  readonly ctx: AudioContext;
  readonly analyser: AnalyserNode;
  private readonly master: GainNode;
  // Live keyboard + ribbon voices route through here so polyphonic input scales
  // by 1/√n (chords don't sum into clipping). Cell-scheduled voices bypass this
  // stage and rely on the master compressor below.
  private readonly liveScale: GainNode;
  // Catch-all soft limiter just before the analyser. Stops harsh clipping when
  // many cell voices stack — much more musical than runaway summation.
  private readonly compressor: DynamicsCompressorNode;
  private project: LooperProject;
  private readonly paletteMap = new Map<string, Patch>();
  private readonly cellGains = new Map<string, GainNode>();
  private readonly nextStart = new Map<string, number>();
  private readonly liveNotes = new Map<number, LiveNote>();
  private timer: number | null = null;

  playing = false;
  /** When false, voices route around the compressor and the live-bus 1/√n
   *  attenuation. Lets you A/B engine-FX vs raw oscillator sum in one click. */
  fxEnabled = true;
  currentPatchId: string;

  /** The box currently focused for recording (may be idle until recording starts). */
  recordCellId: string | null = null;
  /** Whether we are actively capturing played notes into the focused box. */
  recording = false;
  /** Palette id snapshotted for the current recording session (freezes the sound). */
  private recordPatchId: string | null = null;
  /** Per-cell loop time origin, so recorded notes align to the playhead. */
  private readonly loopOrigin = new Map<string, number>();

  // A single sustained voice driven by the frequency ribbon (glides, no retrigger).
  private ribbonVoice: Voice | null = null;
  private ribbonStart = 0;
  private ribbonHitMidi = 60;
  private ribbonPatchId = "";

  /** Called when a hit is recorded into a cell, so the UI can redraw. */
  onCellChanged?: (cellId: string) => void;

  constructor(project: LooperProject) {
    this.project = project;
    this.ctx = new AudioContext();
    this.master = new GainNode(this.ctx, { gain: 0.9 });
    this.liveScale = new GainNode(this.ctx, { gain: 1 });
    this.compressor = new DynamicsCompressorNode(this.ctx, {
      threshold: -18, // start compressing well before 0 dB
      knee: 8,        // soft knee — musical, not surgical
      ratio: 6,       // firm enough to catch stacks, light enough to feel natural
      attack: 0.005,
      release: 0.12,
    });
    this.analyser = new AnalyserNode(this.ctx, { fftSize: 2048 });
    // Chain: liveScale ─┐                         analyser → destination
    //                   ├─► master → compressor ─►
    //   cellGains ─────┘
    this.liveScale.connect(this.master);
    this.master.connect(this.compressor);
    this.compressor.connect(this.analyser);
    this.analyser.connect(this.ctx.destination);
    this.currentPatchId = project.palette[0]?.id ?? "";
    this.refreshPalette();
  }

  // Smooth 1/√n attenuation on the live input bus so chords stay loud without
  // summing into clipping. n = currently sounding keyboard + ribbon voices.
  // When FX is off the bus stays at unity (matches a naive consumer sum).
  private updateLiveScale(): void {
    const target = this.fxEnabled
      ? 1 / Math.sqrt(Math.max(1, this.liveNotes.size + (this.ribbonVoice ? 1 : 0)))
      : 1;
    this.liveScale.gain.setTargetAtTime(target, this.ctx.currentTime, 0.02);
  }

  /** Bypass / re-engage the compressor and live-bus voice scaling. The chain
   *  is rewired live; existing voices stay running. */
  setFxEnabled(enabled: boolean): void {
    if (this.fxEnabled === enabled) return;
    this.fxEnabled = enabled;
    this.master.disconnect();
    this.compressor.disconnect();
    if (enabled) {
      this.master.connect(this.compressor);
      this.compressor.connect(this.analyser);
    } else {
      this.master.connect(this.analyser);
    }
    this.updateLiveScale();
  }

  setProject(project: LooperProject): void {
    this.project = project;
    this.refreshPalette();
    for (const id of [...this.cellGains.keys()]) {
      if (!project.cells.some((c) => c.id === id)) {
        this.cellGains.get(id)?.disconnect();
        this.cellGains.delete(id);
        this.nextStart.delete(id);
        this.loopOrigin.delete(id);
      }
    }
    if (!this.paletteMap.has(this.currentPatchId)) {
      this.currentPatchId = project.palette[0]?.id ?? "";
    }
    if (this.recordCellId && !project.cells.some((c) => c.id === this.recordCellId)) {
      this.recordCellId = null;
      this.recording = false;
    }
  }

  get currentProject(): LooperProject {
    return this.project;
  }

  private refreshPalette(): void {
    this.paletteMap.clear();
    for (const entry of this.project.palette) this.paletteMap.set(entry.id, entry.patch);
  }

  private beat(): number {
    return 60 / this.project.bpm;
  }
  private cellDuration(cell: Cell): number {
    return (cell.lengthBeats * this.beat()) / cell.speed;
  }

  async resume(): Promise<void> {
    if (this.ctx.state !== "running") await this.ctx.resume();
  }

  private cellGain(id: string): GainNode {
    let g = this.cellGains.get(id);
    if (!g) {
      g = new GainNode(this.ctx, { gain: 0.8 });
      g.connect(this.master);
      this.cellGains.set(id, g);
    }
    return g;
  }

  // ---- Live play (keyboard) ----
  noteOn(midi: number, patchId?: string): void {
    void this.resume();
    const id = patchId ?? this.currentPatchId;
    const patch = this.paletteMap.get(id);
    if (!patch) return;
    this.noteOff(midi);
    const voice = new Voice(this.ctx, patch, this.liveScale);
    voice.start(this.ctx.currentTime, midiToFreq(midi), patch.ampEnv);
    this.liveNotes.set(midi, { voice, start: this.ctx.currentTime, patchId: id });
    this.updateLiveScale();
  }

  noteOff(midi: number): void {
    const note = this.liveNotes.get(midi);
    if (!note) return;
    note.voice.stop(this.ctx.currentTime);
    this.liveNotes.delete(midi);
    if (this.recording) {
      this.pushHit(note.patchId, note.start, this.ctx.currentTime - note.start, midi);
    }
    this.updateLiveScale();
  }

  // ---- Frequency ribbon (single gliding voice) ----
  ribbonDown(midi: number, patchId?: string): void {
    void this.resume();
    const id = patchId ?? this.currentPatchId;
    const patch = this.paletteMap.get(id);
    if (!patch) return;
    this.stopRibbon(false);
    const voice = new Voice(this.ctx, patch, this.liveScale);
    voice.start(this.ctx.currentTime, midiToFreq(midi), patch.ampEnv);
    this.ribbonVoice = voice;
    this.ribbonStart = this.ctx.currentTime;
    this.ribbonHitMidi = midi;
    this.ribbonPatchId = id;
    this.updateLiveScale();
  }

  ribbonMove(midi: number): void {
    if (!this.ribbonVoice) return;
    this.ribbonVoice.setFrequency(midiToFreq(midi));
  }

  ribbonUp(): void {
    this.stopRibbon(true);
  }

  private stopRibbon(capture: boolean): void {
    if (!this.ribbonVoice) return;
    this.ribbonVoice.stop(this.ctx.currentTime);
    if (capture && this.recording) {
      this.pushHit(this.ribbonPatchId, this.ribbonStart, this.ctx.currentTime - this.ribbonStart, this.ribbonHitMidi);
    }
    this.ribbonVoice = null;
    this.updateLiveScale();
  }

  private pushHit(patchId: string, startTime: number, dur: number, midi: number): void {
    const cell = this.project.cells.find((c) => c.id === this.recordCellId);
    if (!cell) return;
    const loopPre = cell.lengthBeats * this.beat();
    const loopDur = loopPre / cell.speed;
    const origin = this.loopOrigin.get(cell.id) ?? startTime;
    // Position the note at the loop PHASE where it was played, so it lines up
    // with the playhead on the next pass.
    const phase = loopDur > 0 ? ((((startTime - origin) % loopDur) + loopDur) % loopDur) / loopDur : 0;
    cell.hits.push({ patchId: this.recordPatchId ?? patchId, time: phase * loopPre, dur: Math.max(0.05, dur), midi, gain: 0.9 });
    this.onCellChanged?.(cell.id);
  }

  // ---- Recording ----
  focusCell(cellId: string | null): void {
    this.recordCellId = cellId;
    if (cellId === null) this.recording = false;
  }

  startRecording(): void {
    if (!this.recordCellId) return;
    // Snapshot the current sound so the box's notes keep that sound even if you
    // keep editing afterwards.
    const src = this.paletteMap.get(this.currentPatchId);
    if (src) {
      const id = `rec-${Date.now()}-${Math.floor(Math.random() * 1e4)}`;
      const patch = structuredClone(src);
      this.project.palette.push({ id, patch });
      this.paletteMap.set(id, patch);
      this.recordPatchId = id;
    } else {
      this.recordPatchId = this.currentPatchId;
    }
    this.recording = true;
    if (!this.playing) this.play();
  }

  stopRecording(): void {
    this.recording = false;
  }

  // ---- Sound design (the current editable patch) ----
  /** The current editable sound; its patch object is mutated live by the controls. */
  editPatch(): Patch | undefined {
    return this.paletteMap.get(this.currentPatchId);
  }

  /** Replace the current editable sound (e.g. after loading from the palette/file). */
  loadEditPatch(patch: Patch): void {
    const entry = this.project.palette.find((p) => p.id === this.currentPatchId);
    if (entry) {
      entry.patch = patch;
      this.refreshPalette();
    }
  }

  setMaster(gain: number): void {
    this.master.gain.setTargetAtTime(gain, this.ctx.currentTime, 0.01);
  }

  /** Read the master gain value (used by offline renders so they match live). */
  get masterValue(): number {
    return this.master.gain.value;
  }

  /** The live master node, so callers that build their own sub-bus (the
   *  arrangement's playback bus) can feed *into* it rather than snapshotting
   *  `masterValue` and jumping straight to the analyser. Routing through it
   *  means the master fader keeps working on already-playing audio. */
  get masterNode(): GainNode {
    return this.master;
  }

  /** Schedule one note of a patch at an absolute time (palette preview / phrases).
   *  Honours `patch.xfadeTo` if present. */
  scheduleNote(patch: Patch, midi: number, when: number, dur: number): void {
    spawnNoteVoice(this.ctx, patch, this.master, when, dur, midi);
  }

  previewPatch(patch: Patch, midi: number): void {
    void this.resume();
    // Crossfade patches preview at 1.0 s so the transition is audible.
    const dur = patch.xfadeTo ? 1.0 : 0.7;
    spawnNoteVoice(this.ctx, patch, this.master, this.ctx.currentTime, dur, midi);
  }

  /** Schedule one full iteration of a cell's hits at `when`. Used by the
   *  Arrangement view to trigger placed chips on a one-shot timeline. */
  playCellOnce(cellId: string, when: number): void {
    const cell = this.project.cells.find((c) => c.id === cellId);
    if (!cell) return;
    this.scheduleInstance(cell, when);
  }

  /** Drop a recorded phrase (its notes + sound) into a box, sizing the loop to fit. */
  loadPhraseIntoCell(
    cellId: string,
    patch: Patch,
    events: { midi: number; time: number; dur: number }[],
    lengthSeconds: number,
  ): void {
    const cell = this.project.cells.find((c) => c.id === cellId);
    if (!cell) return;
    const id = `ph-${Date.now()}-${Math.floor(Math.random() * 1e4)}`;
    const p = structuredClone(patch);
    this.project.palette.push({ id, patch: p });
    this.paletteMap.set(id, p);
    cell.hits = events.map((e) => ({ patchId: id, time: e.time, dur: e.dur, midi: e.midi, gain: 0.9 }));
    // Only the "free" meter resizes to fit the dropped phrase. Fixed meters
    // (4/4, 3/4) keep their beat count so two 4/4 boxes always stay in sync —
    // the phrase just plays at its recorded times inside the existing window.
    if (cell.meter === "free" && lengthSeconds > 0) {
      cell.lengthBeats = Math.max(1, Math.round(lengthSeconds / this.beat()));
    }
    this.onCellChanged?.(cellId);
  }

  /** Snapshot a patch into the project palette and return its id (so notes can
   *  reference it). Used when assigning a palette sound to notes in the editor. */
  registerSound(patch: Patch): string {
    const id = `snd-${Date.now()}-${Math.floor(Math.random() * 1e4)}`;
    const p = structuredClone(patch);
    this.project.palette.push({ id, patch: p });
    this.paletteMap.set(id, p);
    return id;
  }

  /** Re-point all of a box's notes at a given sound (drop a patch onto a box). */
  setCellSound(cellId: string, patch: Patch): void {
    const cell = this.project.cells.find((c) => c.id === cellId);
    if (!cell) return;
    const id = `snd-${Date.now()}-${Math.floor(Math.random() * 1e4)}`;
    const p = structuredClone(patch);
    this.project.palette.push({ id, patch: p });
    this.paletteMap.set(id, p);
    for (const h of cell.hits) h.patchId = id;
    this.onCellChanged?.(cellId);
  }

  // ---- Transport ----
  play(): void {
    void this.resume();
    if (this.playing) return;
    this.playing = true;
    const t0 = this.ctx.currentTime + 0.1;
    for (const cell of this.project.cells) {
      this.nextStart.set(cell.id, t0);
      this.loopOrigin.set(cell.id, t0);
    }
    this.tick();
    this.timer = window.setInterval(() => this.tick(), TICK_MS);
  }

  stop(): void {
    this.playing = false;
    this.recording = false;
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  toggle(): void {
    if (this.playing) this.stop();
    else this.play();
  }

  private tick(): void {
    const now = this.ctx.currentTime;
    const horizon = now + LOOKAHEAD;
    for (const cell of this.project.cells) {
      this.cellGain(cell.id).gain.setTargetAtTime(cell.muted ? 0 : cell.gain, now, 0.02);
      let next = this.nextStart.get(cell.id) ?? now + 0.1;
      if (!this.loopOrigin.has(cell.id)) this.loopOrigin.set(cell.id, next);
      const dur = this.cellDuration(cell);
      while (next < horizon) {
        this.scheduleInstance(cell, next);
        next += dur;
      }
      this.nextStart.set(cell.id, next);
    }
  }

  private scheduleInstance(cell: Cell, start: number): void {
    if (cell.muted) return;
    const dest = this.cellGain(cell.id);
    for (const hit of cell.hits) {
      const when = start + hit.time / cell.speed;
      if (when < this.ctx.currentTime - 0.005) continue;
      const patch = this.paletteMap.get(hit.patchId);
      if (!patch) continue;
      spawnNoteVoice(
        this.ctx, patch, dest, when,
        Math.max(0.05, hit.dur) / cell.speed,
        hit.midi + cell.transpose,
        hit.gain ?? 1,
      );
    }
  }

  /** 0..1 position of a cell's playhead, for the UI. */
  getCellPhase(cell: Cell): number {
    if (!this.playing) return 0;
    const next = this.nextStart.get(cell.id);
    if (next === undefined) return 0;
    const dur = this.cellDuration(cell);
    const p = (this.ctx.currentTime - (next - dur)) / dur;
    return Math.min(1, Math.max(0, p));
  }
}
